import { useParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { ColDef } from 'ag-grid-community';

import { useModalWindowApi } from '@/components/ModalWindowApi/useModalWindowApi';
import useGetContractDetailsFromCache from '@/hooks/contract/useGetContractDetailsFromCache.hook';
import { getBillingIntervals } from '@/utils/data-helpers/intervals';
import useGetProducts from '../hooks/useGetProducts';
import { useUpdateFullBCContractLine } from '../hooks/useUpdateFullBCContractLine';
import { getBCContractLineColumns } from './bccontractLinescolumns';
import { ContractLineBillingCycleOutput } from '@/graphql/contracts/_generated_/contracts';
import { getUnitPriceFriquencies } from '@/utils/data-helpers/unitPriceFriquencies';
import { getBillingTypes } from '@/utils/data-helpers/billingTypes';
import useSearchCustomers from '@/pages/AddContract/hooks/useSearchCustomers';
import { useBulkUpdateContractLineShipToCustomer } from '../hooks/useBulkUpdateContractLineShipToCustomer';
import { useBulkUpdateContractLineInvoiceToCustomer } from '../hooks/useBulkUpdateContractLineInvoiceToCustomer';
import { useBulkAssignPurchaseOrder } from '../hooks/useBulkAssignPurchaseOrder';
import useGetPurchaseOrders from '../hooks/useGetPurchaseOrders';
import { useBulkUnassignPurchaseOrder } from '../hooks/useBulkUnassignPurchaseOrder';
import { useBulkUpdateProRataEndDate } from '../hooks/useBulkUpdateProRataEndDate';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';
import { UserRole } from '@/config/roles.config';

export function useColumns(
  onOpenPauseContractLineModal?: (contractLine: ContractLineBillingCycleOutput) => void,
  onOpenDnrModal?: (contractLine: ContractLineBillingCycleOutput) => void,
  onOpenTerminatingModal?: (contractLine: ContractLineBillingCycleOutput) => void
) {
  const { contractId }: { contractId: UUID } = useParams();
  const { getUserRole } = useGetUserRole();
  const [userRole, setUserRole] = useState<UserRole>();

  useEffect(() => {
    const getRole = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };
    getRole();
  }, [getUserRole]);
  const contract = useGetContractDetailsFromCache(contractId);
  const updateBCContractLine = useUpdateFullBCContractLine(contractId);
  const { addModal } = useModalWindowApi();
  const { getProducts } = useGetProducts();
  const searchShipToCustomer = useSearchCustomers();
  const searchInvoiceToCustomer = useSearchCustomers();
  const searchPurchaseOrders = useGetPurchaseOrders();
  const { bulkUpdateContractLineShipToCustomer } = useBulkUpdateContractLineShipToCustomer(contractId);
  const { bulkUpdateContractLineInvoiceToCustomer } = useBulkUpdateContractLineInvoiceToCustomer(contractId);
  const { bulkAssignPurchaseOrder } = useBulkAssignPurchaseOrder(true, contractId, true);
  const { bulkUnassignPurchaseOrder } = useBulkUnassignPurchaseOrder(true, contractId, true);
  const { bulkUpdateProRataEndDate } = useBulkUpdateProRataEndDate(contractId);

  const billingIntervals = useMemo<Pick<BillingInterval, 'billingInterval' | 'formattedBillingInterval'>[]>(() => [
    ...getBillingIntervals(),
    {
      billingInterval: 'add-new-interval',
      formattedBillingInterval: 'Add Interval',
    }
  ], []);

  const unitPriceFrequencies = getUnitPriceFriquencies();
  const billingTypes = getBillingTypes();

  return useMemo<ColDef<ContractLineBillingCycleOutput>[]>(() => (
    getBCContractLineColumns({
      contract, updateBCContractLine, addModal, getProducts, billingIntervals, unitPriceFrequencies, billingTypes,
      searchShipToCustomer, searchInvoiceToCustomer, searchPurchaseOrders,
      bulkUpdateContractLineShipToCustomer, bulkUpdateContractLineInvoiceToCustomer,
      bulkAssignPurchaseOrder, bulkUnassignPurchaseOrder, bulkUpdateProRataEndDate,
      onOpenPauseContractLineModal,
      onOpenDnrModal,
      onOpenTerminatingModal,
      userRole
    })
  ),
  [contract, updateBCContractLine, addModal, getProducts, billingIntervals, unitPriceFrequencies, billingTypes,
    searchShipToCustomer, searchInvoiceToCustomer, searchPurchaseOrders,
    bulkUpdateContractLineShipToCustomer, bulkUpdateContractLineInvoiceToCustomer,
    bulkAssignPurchaseOrder, bulkUnassignPurchaseOrder, bulkUpdateProRataEndDate,
    onOpenPauseContractLineModal, onOpenDnrModal, onOpenTerminatingModal, userRole]);
}
